import React from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import clsx from 'clsx';
import { FieldValues, UseFormHandleSubmit } from 'react-hook-form';

import { Button, Separator, Typography } from 'components/common';
import NextAuthProvidersEnum from '@/const/auth';
import Icons from '@/const/icons';
import AppRoutes from '@/const/routes';
import Image from 'next/image';
import Images from '@/const/images';
import { BoxAuthCard } from './BoxAuthCard';

const Recaptcha = dynamic(
	() => import('@/components/common/recaptcha/ReCaptchaComponent'),
	{
		ssr: false,
	}
);

interface Props {
	title: string;
	disabled: boolean;
	buttonLabel: string;
	isLoading: boolean;
	hideSSO?: boolean;
	hideRecaptcha?: boolean;
	// recaptchaValid: boolean | null;
	onChangeRecaptcha: () => void;
	handleSubmit: UseFormHandleSubmit<FieldValues, FieldValues>;
	handleSubmitData: (
		provider: NextAuthProvidersEnum,
		data?: FieldValues
	) => void;
	children: React.ReactNode;
	formId?: string;
}

export const AuthCard: React.FC<Props> = ({
	title,
	buttonLabel,
	isLoading,
	disabled,
	hideRecaptcha = false,
	hideSSO = false,
	handleSubmit,
	handleSubmitData,
	onChangeRecaptcha,
	children,
	formId,
}) => {
	const { pathname, query } = useRouter();
	const isStaging = process.env.NEXT_PUBLIC_SERVER === 'staging';
	const isLogin = pathname.includes(AppRoutes.CLIENTAPP_AUTH_SIGN_IN);
	const isSignUp = pathname.includes(AppRoutes.CLIENTAPP_AUTH_SIGN_UP);
	const signUpHref =
		query.from === 'register'
			? AppRoutes.CLIENTAPP_AUTH_REGISTER
			: AppRoutes.CLIENTAPP_AUTH_SIGN_UP;

	return (
		<BoxAuthCard>
			<Image
				src={Images.hatLogo}
				alt="Logo"
				width={42}
				height={42}
				className="m-auto mb-3 md:hidden"
			/>
			<Typography type="bold-36" className="text-center">
				{title}
			</Typography>
			{!hideSSO && (
				<>
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-4 3xl:mb-6 mt-6">
						<Button
							size="full"
							onClick={() => handleSubmitData(NextAuthProvidersEnum.GOOGLE)}
							disabled={isLoading}
							icon={Icons.google}
							iconLeft
							label={isSignUp ? 'Sign up with Google' : 'Sign in with Google'}
							className="h-12 md:h-14 shadow-shadow2 md:shadow-none"
							classNameButton="!size-5"
						/>
						<Button
							size="full"
							onClick={() => handleSubmitData(NextAuthProvidersEnum.MICROSOFT)}
							disabled={isLoading}
							icon={Icons.microsoft}
							iconLeft
							label={
								isSignUp ? 'Sign up with Microsoft' : 'Sign in with Microsoft'
							}
							className="h-12 md:h-14 shadow-shadow2 md:shadow-none"
							classNameButton="!size-5"
						/>
					</div>
					<Separator
						text={isSignUp ? 'Or Register With' : 'Or'}
						className="my-4 3xl:my-8"
						classNameText="!bg-[#F5F5F6] shadow-shadow2 md:!bg-white md:shadow-none"
					/>
				</>
			)}
			<form
				id={formId}
				className="w-full"
				onSubmit={handleSubmit((data) =>
					handleSubmitData(NextAuthProvidersEnum.CREDENTIALS, data)
				)}
			>
				{children}
				<div className="flex flex-col justify-center items-center">
					<div
						className={clsx('relative z-20 mt-6', {
							hidden: isStaging || hideRecaptcha,
						})}
					>
						<Recaptcha
							onChangeRecaptcha={onChangeRecaptcha}
							siteKey={process.env.NEXT_PUBLIC_API_RECAPTCHA || ''}
							className="!block !w-auto"
						/>
					</div>
					{isSignUp && (
						<Typography
							type="regular-12"
							className="text-black text-center mt-2"
						>
							By creating your account you are agreeing with <br /> our{' '}
							<Link href="/terms-of-use" target="_blank" className="underline">
								Terms of Use{' '}
							</Link>
							and{' '}
							<Link
								href="/privacy-policy"
								target="_blank"
								className="underline"
							>
								Privacy Policy
							</Link>
							.
						</Typography>
					)}
				</div>
				<div className="flex flex-col gap-4 items-center justify-center mt-6">
					<Button
						type="submit"
						size="full"
						decoration="primary"
						label={buttonLabel}
						loading={isLoading}
						className={clsx('h-12 md:h-16 md:max-w-[231px] bg-purple-150', {
							'hover:!bg-[#B18EE0] bg-[#B18EE0] !border-[#B18EE0]': disabled,
						})}
						disabled={disabled}
					/>
					{isLogin && (
						<Typography type="regular-16" className="text-gray-35">
							Your information is secure and only used to check eligibility.
						</Typography>
					)}
					{isLogin ? (
						<Typography type="regular-16">
							Don’t have an account?{' '}
							<Link href={signUpHref} className="text-purple-150 font-semibold">
								Sign Up
							</Link>
						</Typography>
					) : (
						<Typography type="regular-16">
							Already have an account?{' '}
							<Link
								href={AppRoutes.CLIENTAPP_AUTH_SIGN_IN}
								className="text-purple-150 font-semibold"
							>
								Sign In
							</Link>
						</Typography>
					)}
				</div>
			</form>
		</BoxAuthCard>
	);
};
